import { DEFAULT_CRYPTO_DECIMALS, ZERO_WEI } from '@kwenta/sdk/constants'
import { PositionSide } from '@kwenta/sdk/types'
import { floorNumber, stripZeros } from '@kwenta/sdk/utils'
import Wei, { wei } from '@kwenta/wei'
import React, { useMemo, memo, useCallback, useState, useEffect } from 'react'
import styled from 'styled-components'

import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import { FlexDivRow } from 'components/layout/flex'
import SelectorButtons from 'components/SelectorButtons'
import Spacer from 'components/Spacer'
import { selectShowPositionModal } from 'state/app/selectors'
import { editClosePositionSizeDelta } from 'state/futures/actions'
import { selectClosePositionOrderInputs, selectPerpsProvider } from 'state/futures/common/selectors'
import { selectEditPositionModalInfo } from 'state/futures/selectors'
import { editCloseSMPositionSizeDelta } from 'state/futures/snxPerpsV2/actions'
import { editCrossMarginCloseAmount } from 'state/futures/snxPerpsV3/actions'
import { PerpsProvider } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

const CLOSE_PERCENT_OPTIONS = ['25%', '50%', '75%', '100%']

type OrderSizingProps = {
	maxNativeValue: Wei
	isMobile?: boolean
}

const ClosePositionSizeInput: React.FC<OrderSizingProps> = memo(({ isMobile, maxNativeValue }) => {
	const dispatch = useAppDispatch()

	const { nativeSizeDelta } = useAppSelector(selectClosePositionOrderInputs)

	const { position } = useAppSelector(selectEditPositionModalInfo)
	const modal = useAppSelector(selectShowPositionModal)
	const marginType = useAppSelector(selectPerpsProvider)
	const [isUserInput, setIsUserInput] = useState(false)

	const initialValue = useMemo(
		() => stripZeros(position?.details.size.abs().toString(DEFAULT_CRYPTO_DECIMALS)),
		[position?.details.size]
	)

	const onSizeChange = useCallback(
		(value: string) => {
			setIsUserInput(true)
			if (modal) {
				if (marginType === PerpsProvider.SNX_V3) {
					dispatch(
						editCrossMarginCloseAmount(
							position?.details.side === PositionSide.LONG ? '-' + value : value
						)
					)
				} else {
					dispatch(
						editCloseSMPositionSizeDelta(
							position?.details.side === PositionSide.LONG ? '-' + value : value
						)
					)
				}
			}
		},
		[dispatch, modal, position?.details.side, marginType]
	)

	useEffect(() => {
		if (modal) {
			if (marginType === PerpsProvider.SNX_V3) {
				dispatch(
					editCrossMarginCloseAmount(
						position?.details.side === PositionSide.LONG ? '-' + initialValue : initialValue
					)
				)
			} else {
				dispatch(
					editCloseSMPositionSizeDelta(
						position?.details.side === PositionSide.LONG ? '-' + initialValue : initialValue
					)
				)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onChangeValue = useCallback(
		(_: any, v: string) => {
			onSizeChange(v)
		},
		[onSizeChange]
	)

	const onSelectPercent = useCallback(
		(index: number) => {
			if (!position?.details?.size || !position.market?.marketKey) return
			const option = CLOSE_PERCENT_OPTIONS[index]
			const percent = Math.abs(Number(option.replace('%', ''))) / 100
			const size =
				percent === 1
					? position.details.size.abs()
					: floorNumber(position.details.size.abs().mul(percent))

			const sizeDelta = position.details?.side === PositionSide.LONG ? wei(size).neg() : wei(size)
			const decimals = sizeDelta.abs().eq(position.details.size.abs()) ? undefined : 4
			dispatch(editClosePositionSizeDelta(stripZeros(sizeDelta.toString(decimals))))
		},
		[dispatch, position?.details?.size, position?.details?.side, position?.market?.marketKey]
	)

	const nativeSizeDeltaWei = useMemo(() => {
		return !nativeSizeDelta || isNaN(Number(nativeSizeDelta)) ? ZERO_WEI : wei(nativeSizeDelta)
	}, [nativeSizeDelta])

	const invalid = nativeSizeDelta !== '' && maxNativeValue.lt(nativeSizeDeltaWei.abs())

	return (
		<OrderSizingContainer>
			<OrderSizingRow>
				<InputTitle>Amount to close</InputTitle>
				<SelectorButtons options={CLOSE_PERCENT_OPTIONS} onSelect={onSelectPercent} />
			</OrderSizingRow>

			<NumericInput
				invalid={invalid}
				dataTestId={'edit-position-size-input' + (isMobile ? '-mobile' : '-desktop')}
				value={
					isUserInput
						? nativeSizeDelta.replace('-', '')
						: nativeSizeDelta.replace('-', '') || initialValue
				}
				placeholder="0.00"
				onChange={onChangeValue}
			/>
			<Spacer height={16} />
		</OrderSizingContainer>
	)
})

const OrderSizingContainer = styled.div``

const OrderSizingRow = styled(FlexDivRow)`
	width: 100%;
	align-items: center;
	margin-bottom: 8px;
	cursor: default;
`

export default ClosePositionSizeInput
